import React, { Fragment, useState } from "react";
import {
  colors2,
  getVal,
  icons,
  makeArrayFromLength,
  MetaBlockV2,
  SkeletonLoader,
  UI_BODY_DF,
} from "oolib";

import { tplBlocks } from "@/importGroups/tplBlocks";
import { blockIsPopulatedConditions } from "@/utils/validation/blockIsPopulatedConditions";
import ErrorStates from "@/components/ErrorStates";
import { useElemInView } from "@/utils/customHooks/useElemInView";
import { getRoutePathToPublishedContent } from "@okf/ootils";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import styled from "styled-components";

const { CaretRight } = icons;

const StyledViewContentButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  /*padding: 1rem;*/
  border-radius: 6px;
`;

const StyledResponseRow = styled.div`
  padding: 0.8rem;
  border-bottom: 1px solid ${colors2.grey10};
  display: flex;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 0.15s;

  &:hover {
    background-color: ${colors2.grey5};
  }
`;

const PivotResponsesLoadingState = () => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: "1rem",
      padding: "1rem 1.4rem",
    }}
  >
    {makeArrayFromLength(10).map(() => (
      <SkeletonLoader style={{ height: "10rem", borderRadius: "6px" }} />
    ))}
  </div>
);

/**
 * DataPivotResponsesList renders a list of responses for a given field.
 *
 * It takes in the following props:
 * - data: an array of objects containing the response data
 * - dataFetchStatus: a string indicating the status of the data fetch ("success", "loading", or "error")
 * - dataFetchError: an object containing information about the error that occurred during the data fetch
 * - activeBlock: an object containing information about the block that is currently being viewed
 * - hasNextPage: a boolean indicating whether there is a next page of responses
 * - isFetchingNextPage: a boolean indicating whether the next page of responses is currently being fetched
 * - fetchNextPage: a function to call when the user wants to view the next page of responses
 *
 * It renders a list of responses, with each response displayed as a block.
 * The block is rendered using the BlockComp component, which is passed the following props:
 * - readOnly: a boolean indicating whether the block should be rendered as read-only
 * - label: a string indicating the label for the block
 * - sublabel: a string indicating the sublabel for the block
 * - invert: a boolean indicating whether the block should be rendered with an inverted background color
 * - textAlignment: a string indicating the text alignment for the block
 * - typeStyle: a string indicating the type style for the block
 * - value: the value to display in the block
 *
 * If there is an error during the data fetch, it renders an ErrorStates component.
 * If there is no data to display, it renders a message indicating that there are no responses for the field.
 */
export const DataPivotResponsesList = ({
  data,
  dataFetchStatus,
  dataFetchError,
  activeBlock,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}) => {
  const { observerRef } = useElemInView({
    callback: fetchNextPage,
    enabled: !!hasNextPage && !isFetchingNextPage,
  });
  const BlockComp = activeBlock && tplBlocks[activeBlock.comp];

  const { SELF_MANAGED_BASE_CONFIGS } = useGetQueryData("platformConfigs");
  
  const blockProps = {
    readOnly: true,
    ...(activeBlock?.props || {}),
    label: undefined, //removing label at the block level cuz we are showing it as the page header
    sublabel: undefined,
    readOnly: true,
    ...(['main.title', 'main.subtitle'].includes(activeBlock.valuePath) ? { 
      invert: false ,
      textAlignment: 'left',
      typeStyle: activeBlock.valuePath === 'main.title'
        ? 'CONTENT_H2'
        : 'CONTENT_H3'
    } : {}),
  };
  
  const flattenedData =
    dataFetchStatus === "success"
      ? data?.pages.reduce((a, b) => [...a, ...b.data], []) || []
      : [];

  if (dataFetchStatus === "loading") {
    return <PivotResponsesLoadingState />;
  }

  if (dataFetchStatus === "error") {
    return (
      <ErrorStates
        errorResponseObject={dataFetchError}
        enableLinkToHomepage={false}
      />
    );
  }

  if (dataFetchStatus !== "success") {
    return null;
  }

  return (
    <Fragment>
      {flattenedData.length === 0 && (
        <div style={{ padding: "1.4rem", textAlign: "center" }}>
          <UI_BODY_DF>There are no responses for this field</UI_BODY_DF>
        </div>
      )}
      {flattenedData.map((d, index) => {
        const value = getVal(d, activeBlock.valuePath);
        const shouldDisplayBlock = blockIsPopulatedConditions(
          activeBlock,
          value
        );

        if (!shouldDisplayBlock) {
          if (flattenedData.length === index + 1) {
            return (
              <div
                ref={
                  hasNextPage && flattenedData.length === index + 1
                    ? observerRef
                    : null
                }
              />
            );
          } else {
            return null;
          }
        }

        return (
          <div
            key={d._id}
            style={{ padding: "0 0.8rem" }}
            ref={
              hasNextPage && flattenedData.length === index + 1
                ? observerRef
                : null
            }
          >
            <StyledResponseRow
              onClick={() => {
                const link =
                  getRoutePathToPublishedContent({
                    contentId: d._id,
                    contentType: d.meta?.kp_content_type,
                    SELF_MANAGED_BASE_CONFIGS,
                  }) + `#TplBlock_${activeBlock.props.id}`;
                window.open(link, "_blank");
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <BlockComp {...blockProps} value={value} />
                <div style={{ marginTop: "0.8rem" }}>
                  <MetaBlockV2
                    data={d}
                    config={[
                      { key: "date", valuePath: "kp_date_published" },
                      {
                        key: "title",
                        valuePath: "main.title",
                      },
                      // {
                      //   key: "text",
                      //   value: "View Content", // or you could use a static text approach
                      //   // icon: "LinkSimple",
                      //   linkConfig: getRoutePathToPublishedContent({
                      //     contentId: d._id,
                      //     contentType: d.meta?.kp_content_type,
                      //     SELF_MANAGED_BASE_CONFIGS
                      //   })+`#TplBlock_${activeBlock.props.id}`
                      // }
                    ]}
                  />
                </div>
              </div>
              <StyledViewContentButton>
                <CaretRight size={16} color={colors2.grey60} />
              </StyledViewContentButton>
            </StyledResponseRow>
          </div>
        );
      })}
      {isFetchingNextPage && <PivotResponsesLoadingState />}
    </Fragment>
  );
};
